import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { fetchComparables, fetchGraphVenues, type ComparableArtist, type GraphVenue } from "@/api/discover";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useArtistScope } from "@/lib/artistContext";
import ComparableArtistsPanel from "./ComparableArtistsPanel";
import DiscoverVenuesPanel from "./DiscoverVenuesPanel";
import { friendlyError } from "./discoverCopy";

type TabId = "artists" | "venues";

const PAGE_DESCRIPTION = "Explore artists and venues to find your next opportunity.";

/** `?tab=venues` / `?tab=artists` deep-links a tab (e.g. Home → Explore venues). */
function parseTab(value: string | null): TabId | null {
  return value === "venues" || value === "artists" ? value : null;
}

/**
 * Discover: comparable artists → where they've played → venues worth saving.
 *
 * Page loads only read stored data (comparables + graph venues). Nothing here
 * contacts an external provider unless the user explicitly asks to update a
 * comparable artist's venue history.
 */
export default function DiscoverPage() {
  const { selectedArtistId } = useArtistScope();
  const artistId = selectedArtistId || null;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = parseTab(searchParams.get("tab"));
  const [tab, setTab] = useState<TabId | null>(null);
  const changeTab = useCallback(
    (next: TabId) => {
      setTab(next);
      // Keep the choice in the URL so refresh and Back restore the same tab.
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [comparables, setComparables] = useState<ComparableArtist[]>([]);
  const [comparablesLoading, setComparablesLoading] = useState(false);
  const [comparablesError, setComparablesError] = useState<string | null>(null);
  const [venues, setVenues] = useState<GraphVenue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [venuesError, setVenuesError] = useState<string | null>(null);
  const [artistFilter, setArtistFilter] = useState<{ id: number; name: string } | null>(null);
  const [comparablesLoadedFor, setComparablesLoadedFor] = useState<string | null>(null);
  const [venuesLoadedFor, setVenuesLoadedFor] = useState<string | null>(null);

  // Ignore responses that arrive after the user switched artists.
  const currentArtistRef = useRef<string | null>(artistId);
  currentArtistRef.current = artistId;

  const loadComparables = useCallback(async () => {
    if (!artistId) return;
    const requested = artistId;
    setComparablesLoading(true);
    setComparablesError(null);
    try {
      const payload = await fetchComparables(requested);
      if (currentArtistRef.current !== requested) return;
      setComparables(payload.comparables ?? []);
    } catch (err) {
      if (currentArtistRef.current !== requested) return;
      setComparablesError(friendlyError(err, "We couldn't load your comparable artists. Please try again."));
    } finally {
      if (currentArtistRef.current === requested) {
        setComparablesLoading(false);
        setComparablesLoadedFor(requested);
      }
    }
  }, [artistId]);

  const loadVenues = useCallback(async () => {
    if (!artistId) return;
    const requested = artistId;
    setVenuesLoading(true);
    setVenuesError(null);
    try {
      const payload = await fetchGraphVenues(requested);
      if (currentArtistRef.current !== requested) return;
      setVenues(payload.venues ?? []);
    } catch (err) {
      if (currentArtistRef.current !== requested) return;
      setVenuesError(friendlyError(err, "We couldn't load venues. Please try again."));
    } finally {
      if (currentArtistRef.current === requested) {
        setVenuesLoading(false);
        setVenuesLoadedFor(requested);
      }
    }
  }, [artistId]);

  useEffect(() => {
    // Reset private state on artist switch so nothing leaks between artists.
    setComparables([]);
    setVenues([]);
    setArtistFilter(null);
    setTab(null);
    if (!artistId) return;
    void loadComparables();
    void loadVenues();
  }, [artistId, loadComparables, loadVenues]);

  // First-use default: start on Artists until there is something to show on Venues.
  // An explicit ?tab= always wins over the default.
  useEffect(() => {
    if (tab !== null || urlTab !== null || !artistId) return;
    if (comparablesLoadedFor !== artistId || venuesLoadedFor !== artistId) return;
    setTab(comparables.length > 0 && venues.length > 0 ? "venues" : "artists");
  }, [tab, urlTab, comparablesLoadedFor, venuesLoadedFor, comparables.length, venues.length, artistId]);

  const handleVenueSaved = (globalVenueId: string) => {
    setVenues((prev) =>
      prev.map((venue) =>
        venue.global_venue_id === globalVenueId
          ? {
              ...venue,
              private_state: {
                ...venue.private_state,
                has_relationship: true,
                saved: true,
                status: venue.private_state.status ?? "saved",
                dismissed: false,
                dismissed_at: null,
              },
            }
          : venue,
      ),
    );
  };

  const handleVenuePitchState = (globalVenueId: string, state: "prospect" | "contacted") => {
    setVenues((prev) =>
      prev.map((venue) =>
        venue.global_venue_id === globalVenueId
          ? {
              ...venue,
              private_state: {
                ...venue.private_state,
                has_relationship: true,
                saved: true,
                status: state,
                dismissed: false,
                dismissed_at: null,
                contacted_at:
                  state === "contacted" ? venue.private_state.contacted_at ?? new Date().toISOString() : venue.private_state.contacted_at ?? null,
              },
            }
          : venue,
      ),
    );
  };

  const handleVenueDismissal = (globalVenueId: string, privateState: GraphVenue["private_state"]) => {
    setVenues((prev) =>
      prev.map((venue) => (venue.global_venue_id === globalVenueId ? { ...venue, private_state: privateState } : venue)),
    );
  };

  if (!artistId) {
    return (
      <div className="aa-page-shell space-y-4">
        <PageHeader title="Discover" description={PAGE_DESCRIPTION} />
        <div className="aa-glass-card rounded-2xl p-6 text-center text-sm text-muted-foreground">
          Select an artist to start discovering venues.
        </div>
      </div>
    );
  }

  return (
    <div className="aa-page-shell space-y-4">
      <PageHeader title="Discover" description={PAGE_DESCRIPTION} />
      <Tabs value={tab ?? urlTab ?? "artists"} onValueChange={(value) => changeTab(value as TabId)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="artists">Artists</TabsTrigger>
          <TabsTrigger value="venues">Venues</TabsTrigger>
        </TabsList>
        <TabsContent value="artists">
          <ComparableArtistsPanel
            artistId={artistId}
            comparables={comparables}
            loading={comparablesLoading}
            error={comparablesError}
            onChanged={loadComparables}
            onEvidenceUpdated={() => void loadVenues()}
            onViewVenues={(comparable) => {
              setArtistFilter({ id: comparable.public_artist.id, name: comparable.public_artist.name });
              changeTab("venues");
            }}
          />
        </TabsContent>
        <TabsContent value="venues">
          <DiscoverVenuesPanel
            artistId={artistId}
            venues={venues}
            comparableCount={comparables.length}
            loading={venuesLoading}
            error={venuesError}
            artistFilter={artistFilter}
            onClearArtistFilter={() => setArtistFilter(null)}
            onVenueSaved={handleVenueSaved}
            onVenuePitchState={handleVenuePitchState}
            onVenueDismissal={handleVenueDismissal}
            onGoToArtists={() => changeTab("artists")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
